import { motion } from "framer-motion";
import { StreakChip } from "@/components/dashboard/StreakChip";
import { useGreeting } from "@/hooks/useGreeting";

export type GreetingProps = {
  name: string | null;
  userId: string | null;
  streakDays: number;
  longestStreak?: number;
  lastSeenIso: string | null;
  isBrandNew: boolean;
};

/**
 * Hero band at the top of the dashboard. Big greeting line + streak
 * chip (the page's signature flourish, per FRONTEND_PLAN section 2.6).
 *
 * The greeting line is randomised per-day-per-user via useGreeting --
 * deterministic within a session so it doesn't flicker on re-renders.
 * Streak milestones (3, 7, 30 days) override the time-of-day pool with
 * earned lines.
 */
export function Greeting({
  name,
  userId,
  streakDays,
  longestStreak,
  lastSeenIso,
  isBrandNew,
}: GreetingProps) {
  const message = useGreeting({
    name,
    userId,
    streakDays,
    lastSeenIso,
    isBrandNew,
  });

  // Caption variants: full version on sm+, short on mobile. The streak
  // chip sits inline-with-the-title on mobile (saves ~80px of vertical
  // hero space), so its caption needs to be terse too.
  const captionFull = longestStreak && longestStreak > streakDays
    ? `day streak · best ${longestStreak}`
    : undefined;

  return (
    <header className="flex items-center gap-3 sm:gap-4">
      <motion.h1
        key={message /* re-animates when the line changes day-over-day */}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
        className="flex-1 min-w-0 font-display text-xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-balance"
      >
        {message}
      </motion.h1>

      {streakDays > 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.32, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          className="flex-shrink-0"
        >
          {/* Compact chip on mobile (just flame + number); full caption on sm+. */}
          <StreakChip
            days={streakDays}
            compact
            className="sm:hidden"
          />
          <StreakChip
            days={streakDays}
            caption={captionFull}
            className="hidden sm:inline-flex"
          />
        </motion.div>
      ) : null}
    </header>
  );
}
